import { PARAM_KEY, ADDON_ID, registerShortcuts } from './chunk-CJEHNHYE.mjs';
import { MINIMAL_VIEWPORTS } from './chunk-LLB4Z3MR.mjs';
import * as React from 'react';
import React__default, { memo, useState, useEffect, useRef, Fragment } from 'react';
import { useParameter, useAddonState, useStorybookApi, addons, types } from '@storybook/manager-api';
import memoize from 'memoizerific';
import { styled, withTheme, Global } from '@storybook/theming';
import { IconButton, WithTooltip, TooltipLinkList } from '@storybook/components';
import { Icon } from '@storybook/components/experimental';

var toList = memoize(50)((items) => [
  ...baseViewports,
  ...Object.entries(items).map(([id, { name, ...rest }]) => ({ ...rest, id, title: name }))
]);
var responsiveViewport = {
  id: "reset",
  title: "Reset viewport",
  styles: null,
  type: "other"
};
var baseViewports = [responsiveViewport];
var toLinks = memoize(50)((list, active, set, state, close) => {
  return list.filter((i) => i.id !== responsiveViewport.id || active.id !== i.id).map((i) => {
    return {
      ...i,
      onClick: () => {
        set({ ...state, selected: i.id });
        close();
      }
    };
  });
});
var wrapperId = "storybook-preview-wrapper";
var flip = ({ width, height, ...styles }) => ({
  ...styles,
  height: width,
  width: height
});
var ActiveViewportSize = styled.div(() => ({
  display: "inline-flex"
}));
var ActiveViewportLabel = styled.div(({ theme }) => ({
  display: "inline-block",
  textDecoration: "none",
  padding: 10,
  fontWeight: theme.typography.weight.bold,
  fontSize: theme.typography.size.s2 - 1,
  lineHeight: "1",
  height: 40,
  border: "none",
  borderTop: "3px solid transparent",
  borderBottom: "3px solid transparent",
  background: "transparent"
}));
var IconButtonWithLabel = styled(IconButton)(() => ({
  display: "inline-flex",
  alignItems: "center"
}));
var IconButtonLabel = styled.div(({ theme }) => ({
  fontSize: theme.typography.size.s2 - 1,
  marginLeft: 10
}));
var getStyles = (prevStyles, styles, isRotated) => {
  if (styles === null) {
    return void 0;
  }
  const result = typeof styles === "function" ? styles(prevStyles) : styles;
  return isRotated ? flip(result) : result;
};
var ViewportTool = memo(
  withTheme(({ theme }) => {
    const {
      viewports = MINIMAL_VIEWPORTS,
      defaultOrientation = "portrait",
      defaultViewport = responsiveViewport.id,
      disable
    } = useParameter(PARAM_KEY, {});
    const [state, setState] = useAddonState(ADDON_ID, {
      selected: defaultViewport,
      isRotated: defaultOrientation === "landscape"
    });
    const list = toList(viewports);
    const api = useStorybookApi();
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    if (!list.find((i) => i.id === defaultViewport)) {
      console.warn(
        `Cannot find "defaultViewport" of "${defaultViewport}" in addon-viewport configs, please check the "viewports" setting in the configuration.`
      );
    }
    useEffect(() => {
      registerShortcuts(api, setState, Object.keys(viewports));
    }, [viewports]);
    useEffect(() => {
      setState({
        selected: defaultViewport || (state.selected && viewports[state.selected] ? state.selected : responsiveViewport.id),
        isRotated: defaultOrientation === "landscape"
      });
    }, [defaultOrientation, defaultViewport]);
    const { selected, isRotated } = state;
    const item = list.find((i) => i.id === selected) || list.find((i) => i.id === defaultViewport) || list.find((i) => i.default) || responsiveViewport;
    const ref = useRef();
    const styles = getStyles(ref.current, item.styles, isRotated);
    useEffect(() => {
      ref.current = styles;
    }, [item]);
    if (disable || Object.entries(viewports).length === 0) {
      return null;
    }
    return /* @__PURE__ */ React__default.createElement(Fragment, null, /* @__PURE__ */ React__default.createElement(
      WithTooltip,
      {
        placement: "top",
        tooltip: ({ onHide }) => /* @__PURE__ */ React__default.createElement(TooltipLinkList, { links: toLinks(list, item, setState, state, onHide) }),
        closeOnOutsideClick: true,
        onVisibleChange: setIsTooltipVisible
      },
      /* @__PURE__ */ React__default.createElement(
        IconButtonWithLabel,
        {
          key: "viewport",
          title: "Change the size of the preview",
          active: isTooltipVisible || !!styles,
          onDoubleClick: () => {
            setState({ ...state, selected: responsiveViewport.id });
          }
        },
        /* @__PURE__ */ React__default.createElement(Icon.Grow, null),
        styles ? /* @__PURE__ */ React__default.createElement(IconButtonLabel, null, isRotated ? `${item.title} (L)` : `${item.title} (P)`) : null
      )
    ), styles ? /* @__PURE__ */ React__default.createElement(ActiveViewportSize, null, /* @__PURE__ */ React__default.createElement(
      Global,
      {
        styles: {
          [`iframe[data-is-storybook="true"]`]: {
            margin: `auto`,
            transition: "none",
            position: "relative",
            border: `1px solid black`,
            boxShadow: "0 0 100px 100vw rgba(0,0,0,0.5)",
            ...styles
          },
          [`#${wrapperId}`]: {
            padding: theme.layoutMargin,
            alignContent: "center",
            alignItems: "center",
            justifyContent: "center",
            justifyItems: "center",
            overflow: "auto",
            display: "grid",
            gridTemplateColumns: "100%",
            gridTemplateRows: "100%"
          }
        }
      }
    ), /* @__PURE__ */ React__default.createElement(ActiveViewportLabel, { title: "Viewport width" }, styles.width.replace("px", "")), /* @__PURE__ */ React__default.createElement(
      IconButton,
      {
        key: "viewport-rotate",
        title: "Rotate viewport",
        onClick: () => {
          setState({ ...state, isRotated: !isRotated });
        }
      },
      /* @__PURE__ */ React__default.createElement(Icon.Transfer, null)
    ), /* @__PURE__ */ React__default.createElement(ActiveViewportLabel, { title: "Viewport height" }, styles.height.replace("px", ""))) : null);
  })
);

// src/manager.tsx
addons.register(ADDON_ID, () => {
  addons.add(ADDON_ID, {
    title: "viewport / media-queries",
    type: types.TOOL,
    match: ({ viewMode }) => viewMode === "story",
    render: () => /* @__PURE__ */ React.createElement(ViewportTool, null)
  });
});
